import React, { useMemo, useState } from "react";
import { UI_TAG, Divider, colors2, UI_BODY } from "oolib";
import { DrilldownDepthContext } from "@/components/UI/DrilldownList/context/DrilldownDepthContext";
import { GroupHeader, GroupDivider } from "@/components/UI/List/comps/ListGroup";
import { PanelItem, FilterListItem, StyledScrollableWrapper, StyledHierarchyItem, StyledConnector, PICKER_ORIGIN } from "./comps/PanelItem";
import { hasFilterValue } from "../../utils/filterConstants";
import { DrilldownList, SearchActionsToolbar } from "@/components/UI/DrilldownList";
import { useInFlightToggle } from "@/utils/customHooks/useInFlightToggle";
import { useLightbox } from "@/components/UI/lightbox";
import { buildRollupTree } from "./utils/buildRollupTree";

const StyledRootWrapper = StyledScrollableWrapper;

const SECTION_LABEL_STYLE = {
  padding: "0.6rem 0.8rem 0.4rem 0.8rem",
  color: colors2.grey50,
};

/**
 * Compute nesting levels from parentFilterId.
 * Assumes filters are sorted so parents appear before children
 * (guaranteed by sortFiltersHierarchically in ootils).
 */
const computeFilterLevels = (filters) => {
  const filterIds = new Set(filters.map((f) => f.filterId));
  const levelMap = {};
  for (const f of filters) {
    if (!f.parentFilterId || !filterIds.has(f.parentFilterId)) {
      // No parent, or parent is in a different list (e.g. rollups) — treat as root
      levelMap[f.filterId] = 0;
    } else {
      levelMap[f.filterId] = (levelMap[f.parentFilterId] ?? 0) + 1;
    }
  }
  return levelMap;
};

/** Wraps FilterListItem with hierarchy indentation + connector when level > 0 */
const HierarchyFilterListItem = ({ filter, level, ...rest }) => {
  if (level > 0) {
    return (
      <StyledHierarchyItem>
        <StyledConnector $level={level} />
        <FilterListItem
          filter={filter}
          {...rest}
          style={{ paddingLeft: `${level * 2 + 0.8}rem` }}
        />
      </StyledHierarchyItem>
    );
  }
  return <FilterListItem filter={filter} {...rest} />;
};

/** Renders a single filter leaf node */
const RollupFilterLeaf = ({ filter, selectedValues, onChange, contentTypes, activeFilters, childFilterConfig }) => (
  <FilterListItem
    filter={{ ...filter, isSharedFilter: false }}
    selectedValues={selectedValues}
    onChange={onChange}
    contentTypes={contentTypes}
    activeFilters={activeFilters}
    childFilterConfig={childFilterConfig}
  />
);

/** Recursively renders a rollup tree (folders + valuePath leaves + tag leaves).
 *  `childByParentId` maps a tag filter's filterId → its immediate child tag filter,
 *  which enables value drilldown (e.g. Country → State) inside the option list. */
const RollupTreeRenderer = ({ nodes, selectedValues, onChange, contentTypes, activeFilters, childByParentId }) => {
  return nodes.map((node) => {
    if (node.type === "filter") {
      return (
        <RollupFilterLeaf
          key={node.filter.filterId}
          filter={node.filter}
          selectedValues={selectedValues}
          onChange={onChange}
          contentTypes={contentTypes}
          activeFilters={activeFilters}
          childFilterConfig={childByParentId?.[node.filter.filterId]}
        />
      );
    }
    if (node.type === "tagFilter") {
      return (
        <HierarchyFilterListItem
          key={node.filter.filterId}
          filter={{ ...node.filter, isSharedFilter: false }}
          level={node.level || 0}
          selectedValues={selectedValues}
          onChange={onChange}
          contentTypes={contentTypes}
          activeFilters={activeFilters}
          childFilterConfig={childByParentId?.[node.filter.filterId]}
        />
      );
    }
    // Folder node — own valuePath fields at top, tag filters indented, sub-folders below
    const hasOwnFilters = node.ownFilters?.length > 0;
    const hasTagFilters = node.tagFilters?.length > 0;
    const hasRollupChildren = node.rollupChildren?.length > 0;

    return (
      <PanelItem
        key={node.contentType}
        option={{ display: node.display, icon: "FolderSimple", hasChildren: true }}
      >
        <StyledScrollableWrapper>
          <div data-lightbox-header>
            <UI_TAG style={SECTION_LABEL_STYLE}>{node.display}</UI_TAG>
          </div>
          {/* Own fields — valuePath filters belonging to this dataset */}
          {hasOwnFilters ? (
            node.ownFilters.map((leaf) => (
              <RollupFilterLeaf
                key={leaf.filter.filterId}
                filter={leaf.filter}
                selectedValues={selectedValues}
                onChange={onChange}
                contentTypes={contentTypes}
                activeFilters={activeFilters}
                childFilterConfig={childByParentId?.[leaf.filter.filterId]}
              />
            ))
          ) : !hasTagFilters ? (
            <div style={{ padding: "0.6rem 1rem", color: colors2.grey40 }}>
              <UI_BODY>No direct filterable fields in this dataset</UI_BODY>
            </div>
          ) : null}
          {/* Tag filters — indented using existing hierarchy UI */}
          {hasTagFilters && (
            <>
              {node.tagFilters.map((tagNode) => (
                <HierarchyFilterListItem
                  key={tagNode.filter.filterId}
                  filter={{ ...tagNode.filter, isSharedFilter: false }}
                  level={tagNode.level || 0}
                  selectedValues={selectedValues}
                  onChange={onChange}
                  contentTypes={contentTypes}
                  activeFilters={activeFilters}
                  childFilterConfig={childByParentId?.[tagNode.filter.filterId]}
                />
              ))}
            </>
          )}
          {/* Rollup children — sub-folders for datasets with valuePath fields */}
          {hasRollupChildren && (
            <>
              {(hasOwnFilters || hasTagFilters) && <Divider style={{ margin: "0.4rem 0" }} />}
              <div data-lightbox-header>
                <UI_TAG style={SECTION_LABEL_STYLE}>Connected Datasets</UI_TAG>
              </div>
              <RollupTreeRenderer
                nodes={node.rollupChildren}
                selectedValues={selectedValues}
                onChange={onChange}
                contentTypes={contentTypes}
                activeFilters={activeFilters}
                childByParentId={childByParentId}
              />
            </>
          )}
        </StyledScrollableWrapper>
      </PanelItem>
    );
  });
};

/**
 * A dataset's Document Field options as checkbox rows, above its other fields.
 * Every dataset's docFieldFilter shares one filterId, so a field ticked here is
 * part of a single Document Field filter across datasets.
 */
const DocFieldRows = ({ filter, searchTerm = "", selectedValues, onChange, withDivider }) => {
  const toggle = useInFlightToggle();
  const value = selectedValues[filter.filterId] || [];
  const q = searchTerm.toLowerCase();
  const options = (filter.source?.options || [])
    .filter((o) => (o.display || "").toLowerCase().includes(q))
    .map((o) => ({ _id: o.value, value: o.value, display: o.display }));
  if (options.length === 0) return null;

  const handleChange = (option) =>
    onChange(
      filter.filterId,
      toggle({
        key: "_",
        fallbackValues: value,
        option,
        buildEntry: (o, id) => ({ value: id, display: o.display }),
      }),
      PICKER_ORIGIN
    );

  return (
    <>
      <DrilldownList
        options={options}
        value={value.map((v) => ({ _id: v.value, value: v.value, display: v.display }))}
        isMulti
        onChange={handleChange}
      />
      {withDivider && <Divider style={{ margin: "0.4rem 0" }} />}
    </>
  );
};

/** Panel content with search bar that filters the list of filter fields */
const SearchableFilterPanel = ({ label, filters, levelMap, selectedValues, onChange, contentTypes, activeFilters }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { setOpen } = useLightbox() || {};
  const filtered = searchTerm
    ? filters.filter((f) => (f.display || "").toLowerCase().includes(searchTerm.toLowerCase()))
    : filters;

  return (
    <>
      <SearchActionsToolbar
        search={{ value: searchTerm, onChange: setSearchTerm }}
        onClose={setOpen ? () => setOpen(false) : undefined}
      />
      <StyledScrollableWrapper>
        {label && <div data-lightbox-header><UI_TAG style={SECTION_LABEL_STYLE}>{label}</UI_TAG></div>}
        {filtered.map((filter) => (
          <HierarchyFilterListItem
            key={filter.filterId}
            filter={filter}
            level={levelMap[filter.filterId] || 0}
            selectedValues={selectedValues}
            onChange={onChange}
            contentTypes={contentTypes}
            activeFilters={activeFilters}
          />
        ))}
      </StyledScrollableWrapper>
    </>
  );
};

/** Dataset panel content with search bar that filters both filters and rollups */
const DatasetPanel = ({ dataset, selectedValues, onChange, activeFilters }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { setOpen } = useLightbox() || {};

  // Own filter levels: computed from filters ONLY (parents must be in the same filter list)
  const filterLevelMap = useMemo(() => computeFilterLevels(dataset.filters || []), [dataset.filters]);
  // Rollup levels: computed from rollups (for tag hierarchy inside the rollup tree)
  const rollupLevelMap = useMemo(() => computeFilterLevels(dataset.rollups || []), [dataset.rollups]);

  // Immediate child of each tag rollup (State→Country, etc.) for value drilldown.
  // Built once in ootils (_self_managed_buildDocHierarchyConfig) and read off the
  // config here, so the folder and flat single-dataset paths share one source.
  const childByParentId = dataset.childByParentId || {};

  const filterItems = searchTerm
    ? (dataset.filters || []).filter((f) => (f.display || "").toLowerCase().includes(searchTerm.toLowerCase()))
    : (dataset.filters || []);

  // Build rollup tree from (optionally search-filtered) flat rollups
  const rollupTree = useMemo(() => {
    const rollups = searchTerm
      ? (dataset.rollups || []).filter((f) => (f.display || "").toLowerCase().includes(searchTerm.toLowerCase()))
      : (dataset.rollups || []);
    return buildRollupTree(rollups, dataset.rollupDisplayMap, rollupLevelMap);
  }, [dataset.rollups, dataset.rollupDisplayMap, searchTerm, rollupLevelMap]);

  return (
    <>
      <SearchActionsToolbar
        search={{ value: searchTerm, onChange: setSearchTerm }}
        onClose={setOpen ? () => setOpen(false) : undefined}
      />
      <StyledScrollableWrapper>
        <div data-lightbox-header><UI_TAG style={SECTION_LABEL_STYLE}>Dataset Fields</UI_TAG></div>
        {dataset.docFieldFilter && (
          <DocFieldRows
            filter={dataset.docFieldFilter}
            searchTerm={searchTerm}
            selectedValues={selectedValues}
            onChange={onChange}
            withDivider={filterItems.length > 0 || rollupTree.length > 0}
          />
        )}
        {filterItems.map((filter) => (
          <HierarchyFilterListItem
            key={filter.filterId}
            filter={{ ...filter, isSharedFilter: false }}
            level={filterLevelMap[filter.filterId] || 0}
            selectedValues={selectedValues}
            onChange={onChange}
            contentTypes={[dataset.contentType]}
            activeFilters={activeFilters}
          />
        ))}
        {rollupTree.length > 0 && (
          <>
            <div data-lightbox-header><UI_TAG style={SECTION_LABEL_STYLE}>Connected Datasets</UI_TAG></div>
            <RollupTreeRenderer
              nodes={rollupTree}
              selectedValues={selectedValues}
              onChange={onChange}
              contentTypes={[dataset.contentType]}
              activeFilters={activeFilters}
              childByParentId={childByParentId}
            />
          </>
        )}
      </StyledScrollableWrapper>
    </>
  );
};

/**
 * DocHierarchyFilter renders a drilldown-based document filter UI.
 *
 * Structure:
 * - Level 0: Section entries ("Shared across all", dataset names, metadata)
 * - Level 1: Filter entries per section (via PanelItem + FilterListItem)
 * - Level 2: Actual filter options (via FilterOptionsPanel + DrilldownList)
 *
 * Props:
 * - filterConfig: The docHierarchyType config from useAutoGenFilterConfigsFromTpl
 * - value: Object of { [filterId]: selectedValues[] }
 * - onChange: (filterId, values) => void
 */
export const DocHierarchyFilter = ({ filterConfig, value, onChange, activeFilters = [] }) => {
  const {
    flatDocFilters = false,
    commonFilters = [],
    sharedAcrossMultiple = [],
    commonDocumentFilters = [],
    perDataset = [],
  } = filterConfig?.source || {};

  // Derive selectedValues from activeFilters so child filters can see their current values
  const selectedValues = useMemo(() => {
    const map = {};
    for (const af of activeFilters) {
      if (hasFilterValue(af.values)) map[af.filterId] = af.values;
    }
    return map;
  }, [activeFilters]);

  // Filter out empty datasets
  const nonEmptyDatasets = useMemo(
    () => perDataset.filter((d) => d.filters?.length > 0 || d.rollups?.length > 0 || d.docFieldFilter),
    [perDataset]
  );

  // All content types across datasets (for common/metadata filters)
  // Falls back to explicit contentTypes when perDataset is empty (e.g. standalone metadata section)
  const allContentTypes = useMemo(
    () => {
      const fromDatasets = perDataset.map((d) => d.contentType).filter(Boolean);
      return fromDatasets.length > 0 ? fromDatasets : (filterConfig?.source?.contentTypes || []);
    },
    [perDataset, filterConfig?.source?.contentTypes]
  );

  // Pre-compute hierarchy levels for all sections
  const commonLevels = useMemo(() => computeFilterLevels(commonFilters), [commonFilters]);

  // Separate level maps: own filter levels vs rollup levels (prevents filterId collisions)
  const perDatasetFilterLevels = useMemo(() => {
    const map = {};
    for (const dataset of nonEmptyDatasets) {
      Object.assign(map, computeFilterLevels(dataset.filters || []));
    }
    return map;
  }, [nonEmptyDatasets]);

  const perDatasetRollupLevels = useMemo(() => {
    const map = {};
    for (const dataset of nonEmptyDatasets) {
      Object.assign(map, computeFilterLevels(dataset.rollups || []));
    }
    return map;
  }, [nonEmptyDatasets]);

  const docFilterLevels = useMemo(
    () => computeFilterLevels(commonDocumentFilters),
    [commonDocumentFilters]
  );

  const commonFilterExists = commonFilters.length > 0;
  const sharedAcrossMultipleExists = sharedAcrossMultiple.length > 0;
  const combinedFilterExists = commonFilterExists || sharedAcrossMultipleExists

  // Flat rendering for single-template listing pages
  if (flatDocFilters && perDataset.length === 1) {
    const dataset = perDataset[0];

    return (
      <DrilldownDepthContext.Provider value={0}>
        <StyledRootWrapper>
          {/* Metadata filters (Author, Published Date, etc.) */}
          {commonDocumentFilters.length > 0 && (
            <>
              <div data-lightbox-header><GroupHeader><UI_TAG>Metadata</UI_TAG></GroupHeader></div>
              {commonDocumentFilters.map((filter) => (
                <HierarchyFilterListItem
                  key={filter.filterId}
                  filter={filter}
                  level={docFilterLevels[filter.filterId] || 0}
                  selectedValues={selectedValues}
                  onChange={onChange}
                  contentTypes={allContentTypes}
                />
              ))}
            </>
          )}
          {/* Dataset filters grouped by content type */}
          {(dataset.filters?.length > 0 || dataset.docFieldFilter) && (
            <>
              {commonDocumentFilters.length > 0 && <GroupDivider><Divider /></GroupDivider>}
              <div data-lightbox-header><GroupHeader><UI_TAG>Dataset Fields</UI_TAG></GroupHeader></div>
              {dataset.docFieldFilter && (
                <DocFieldRows
                  filter={dataset.docFieldFilter}
                  selectedValues={selectedValues}
                  onChange={onChange}
                  withDivider={dataset.filters?.length > 0}
                />
              )}
              {(dataset.filters || []).map((filter) => (
                <HierarchyFilterListItem
                  key={filter.filterId}
                  filter={{ ...filter, isSharedFilter: false }}
                  level={perDatasetFilterLevels[filter.filterId] || 0}
                  selectedValues={selectedValues}
                  onChange={onChange}
                  contentTypes={[dataset.contentType]}
                  activeFilters={activeFilters}
                />
              ))}
            </>
          )}
          {/* Rollup filters — rendered as hierarchical folders */}
          {dataset.rollups?.length > 0 && (() => {
            const tree = buildRollupTree(dataset.rollups, dataset.rollupDisplayMap, perDatasetRollupLevels);
            return tree.length > 0 ? (
              <>
                <GroupDivider><Divider /></GroupDivider>
                <div data-lightbox-header><GroupHeader><UI_TAG>Connected Datasets</UI_TAG></GroupHeader></div>
                <RollupTreeRenderer
                  nodes={tree}
                  selectedValues={selectedValues}
                  onChange={onChange}
                  contentTypes={[dataset.contentType]}
                  activeFilters={activeFilters}
                  childByParentId={dataset.childByParentId}
                />
              </>
            ) : null;
          })()}
        </StyledRootWrapper>
      </DrilldownDepthContext.Provider>
    );
  }

  return (
    <DrilldownDepthContext.Provider value={0}>
      <StyledRootWrapper>
        { commonFilterExists &&
        <div data-lightbox-header><UI_TAG style={SECTION_LABEL_STYLE}>Shared Filters</UI_TAG></div> }
        {commonFilterExists && (
          <PanelItem option={{ display: "Shared across all datasets", icon: "Funnel", hasChildren: true }}>
            <SearchableFilterPanel
              label="Shared across all"
              filters={commonFilters}
              levelMap={commonLevels}
              selectedValues={selectedValues}
              onChange={onChange}
              contentTypes={allContentTypes}
              activeFilters={activeFilters}
            />
          </PanelItem>
        )}

        {/* CONTENT FILTERS (DATASETS) section */}
        {commonFilterExists && nonEmptyDatasets.length > 0 && <Divider style={{ margin: "0.4rem 0" }} />}
        {nonEmptyDatasets.length > 0 && (
          <>
            <div data-lightbox-header><UI_TAG style={SECTION_LABEL_STYLE}>Content Filters (Datasets)</UI_TAG></div>
            {nonEmptyDatasets.map((dataset) => (
              <PanelItem
                key={dataset.contentType}
                option={{
                  display: dataset.display,
                  icon: "FolderSimple",
                  hasChildren: true,
                  disabledReason: dataset.disabledReason,
                }}
              >
                <DatasetPanel
                  dataset={dataset}
                  selectedValues={selectedValues}
                  onChange={onChange}
                  activeFilters={activeFilters}
                />
              </PanelItem>
            ))}
          </>
        )}

        {/* METADATA section - Author, Published Date, etc. */}
        {(combinedFilterExists || nonEmptyDatasets.length > 0) && commonDocumentFilters.length > 0 && <Divider style={{ margin: "0.4rem 0" }} />}
        {commonDocumentFilters.length > 0 && (
          <>
            <div data-lightbox-header><UI_TAG style={SECTION_LABEL_STYLE}>Metadata</UI_TAG></div>
            {commonDocumentFilters.map((filter) => (
              <HierarchyFilterListItem
                key={filter.filterId}
                filter={filter}
                level={docFilterLevels[filter.filterId] || 0}
                selectedValues={selectedValues}
                onChange={onChange}
                contentTypes={allContentTypes}
              />
            ))}
          </>
        )}
      </StyledRootWrapper>
    </DrilldownDepthContext.Provider>
  );
};
